import { useState } from "react";
import { useAdminData } from "../state/AdminDataContext";
import { fmtAuditTime } from "../lib/format";
import { PageHeaderActions } from "../components/PageHeaderActions";
import { downloadCsv } from "../lib/csv";

// Grey tile, coloured glyph (canvas v2-a-audit).
const META: Record<string, { icon: string; kind: "payments" | "plans" | "config"; color: string }> = {
  "Payment submitted": { icon: "upload", kind: "payments", color: "var(--blue)" },
  "Payment approved": { icon: "verified", kind: "payments", color: "var(--ok)" },
  "Payment rejected": { icon: "block", kind: "payments", color: "var(--coral)" },
  "Reopened for re-submission": { icon: "replay", kind: "payments", color: "var(--amber)" },
  "Subscription activated": { icon: "workspace_premium", kind: "plans", color: "var(--ink)" },
  "Plan updated": { icon: "edit", kind: "plans", color: "var(--text2)" },
  "Configuration changed": { icon: "tune", kind: "config", color: "var(--text2)" },
  "Location check bypass": { icon: "wrong_location", kind: "config", color: "var(--amber)" },
};

const TABS = [
  { key: "all", label: "All" },
  { key: "payments", label: "Payments" },
  { key: "plans", label: "Plans" },
  { key: "config", label: "Configuration" },
] as const;

export function AuditPage() {
  const { auditLog } = useAdminData();
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("all");
  const rows = auditLog.filter((a) => tab === "all" || (META[a.action]?.kind ?? "config") === tab).slice(0, 100);

  return (
    <div>
      <PageHeaderActions>
        <button
          type="button"
          className="o-btn o-btn--line"
          disabled={auditLog.length === 0}
          onClick={() =>
            downloadCsv(
              "audit-log.csv",
              ["Time", "Action", "Detail", "By", "Payment"],
              auditLog.map((a) => [a.created_at, a.action, a.detail ?? "", a.actor_name ?? "System", a.payment_ref ?? ""]),
            )
          }
        >
          <span className="ms" aria-hidden="true">download</span>
          Export
        </button>
      </PageHeaderActions>

      <div className="o-chips" role="tablist">
        {TABS.map((t) => (
          <button type="button" role="tab" aria-selected={tab === t.key} aria-checked={tab === t.key} key={t.key} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      <section className="o-card">
        {rows.length === 0 && (
          <div className="o-empty">
            <span className="ms" aria-hidden="true">history</span>
            <strong>Nothing logged yet</strong>
          </div>
        )}
        {rows.map((a) => {
          const m = META[a.action] ?? { icon: "bolt", color: "var(--text2)" };
          return (
            <div key={a.id} className="o-list-row">
              <span className="o-ico" style={{ color: m.color }}><span className="ms" aria-hidden="true">{m.icon}</span></span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="o-row__title">{a.action}</div>
                <div className="o-row__sub" style={{ whiteSpace: "normal" }}>
                  {a.actor_name ?? "System"}
                  {a.detail ? `: ${a.detail}` : ""}
                  {a.payment_ref ? `, ${a.payment_ref}` : ""}
                </div>
              </div>
              <span className="o-list-row__state" style={{ whiteSpace: "nowrap" }}>{fmtAuditTime(a.created_at)}</span>
            </div>
          );
        })}
      </section>
    </div>
  );
}
